import { lazy, Suspense, useState } from "react";
import { useIntl } from "react-intl";

import { Drawer, DrawerContent, DrawerTopBar } from "@/components/Drawer";
import { MruCountryProvider } from "@/components/Inputs/TelInput";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TravellerCloseConfirmDrawer } from "@/features/travellers/components/TravellerCloseConfirmDrawer";
import {
  useNewTravellerMutation,
  useUpdateTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";
import type { TravellerAgeGroupConstraint } from "@/features/travellers/utils/validation";

import { useTravellerForm } from "./useTravellerForm";

const TravellerForm = lazy(
  () => import("@/features/travellers/components/TravellerForm"),
);

type TravellerFormDrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  traveller: Traveller | null;
  onSubmitSuccess: (traveller: Traveller) => void;
  ageGroupConstraint?: TravellerAgeGroupConstraint;
};

export function TravellerFormDrawer({
  open,
  onClose,
  title,
  traveller,
  onSubmitSuccess,
  ageGroupConstraint,
}: TravellerFormDrawerProps) {
  const intl = useIntl();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const handleMutationSuccess = (traveller: Traveller) => {
    onSubmitSuccess(traveller);
  };
  const { mutateAsync: addTraveller, isPending: isAddPending } =
    useNewTravellerMutation({
      onSuccess: handleMutationSuccess,
    });
  const { mutateAsync: editTraveller, isPending: isEditPending } =
    useUpdateTravellerMutation({
      onSuccess: handleMutationSuccess,
    });

  const {
    control,
    isSubmitting,
    requestClose,
    scanned,
    scanningDrawerOpen,
    openScanningDrawer,
    closeScanningDrawer,
    onScanSuccess,
    onFormSubmit,
  } = useTravellerForm({
    traveller,
    ageGroupConstraint,
    onSubmit: (data) =>
      traveller
        ? editTraveller({ travellerId: traveller.id, data })
        : addTraveller(data),
    onClose: (reason) => {
      if (reason === "cancelWithPendingChanges") {
        setIsCloseConfirmOpen(true);
        return;
      }

      onClose();
    },
  });

  return (
    <>
      <Drawer
        open={open}
        disableRestoreFocus
        hasFullHeight
        backgroundColor="background"
      >
        <DrawerTopBar
          onCloseClick={requestClose}
          title={
            title ||
            (traveller
              ? intl.formatMessage({
                  defaultMessage: "Edit traveller",
                  description: "Button to edit a saved traveller's details",
                })
              : intl.formatMessage({
                  defaultMessage: "Add traveller",
                  description:
                    "Button label to add a traveller to a booking slot",
                }))
          }
        />
        <DrawerContent>
          <Suspense
            fallback={
              <PageLoader>
                <AnimatedLoader icon="users" />
              </PageLoader>
            }
          >
            <MruCountryProvider>
              <TravellerForm
                control={control}
                isSubmitting={isSubmitting || isAddPending || isEditPending}
                scanned={scanned}
                scanningDrawerOpen={scanningDrawerOpen}
                onScanClick={openScanningDrawer}
                onScanningDrawerClose={closeScanningDrawer}
                onScanSuccess={onScanSuccess}
                onFormSubmit={onFormSubmit}
              />
            </MruCountryProvider>
          </Suspense>
        </DrawerContent>
      </Drawer>

      <TravellerCloseConfirmDrawer
        open={isCloseConfirmOpen}
        onClose={() => {
          setIsCloseConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCloseConfirmOpen(false);
          onClose();
        }}
      />
    </>
  );
}
